/**
 * MODULE: domain/pages/frontend/utils/pagePermissionUtils.ts
 * PURPOSE: Labels and helpers for page permission UI
 */

import type { EffectivePagePermission, PermissionLevel } from '../../contracts/pages.types';

const PERMISSION_RANK: Record<PermissionLevel, number> = {
  view: 1,
  comment: 2,
  edit: 3,
  full: 4,
};

export const PERMISSION_LEVEL_OPTIONS: Array<{ value: PermissionLevel; label: string; description: string }> = [
  { value: 'view', label: 'Can view', description: 'Read-only access to this page' },
  { value: 'comment', label: 'Can comment', description: 'View the page and add comments' },
  { value: 'edit', label: 'Can edit', description: 'Edit page content and database rows' },
  { value: 'full', label: 'Full access', description: 'Edit, share, and delete this page' },
];

export function permissionLevelLabel(level: PermissionLevel): string {
  return PERMISSION_LEVEL_OPTIONS.find((option) => option.value === level)?.label ?? level;
}

export function permissionRank(effective: EffectivePagePermission | undefined): number {
  if (!effective) {
    return PERMISSION_RANK.edit;
  }
  return PERMISSION_RANK[effective.permission_level];
}

export function canManagePagePermissions(effective: EffectivePagePermission | undefined): boolean {
  if (!effective) {
    return false;
  }
  return effective.is_owner || effective.permission_level === 'full';
}

export function canCommentPage(effective: EffectivePagePermission | undefined): boolean {
  return permissionRank(effective) >= PERMISSION_RANK.comment;
}

export function canEditPage(effective: EffectivePagePermission | undefined): boolean {
  return permissionRank(effective) >= PERMISSION_RANK.edit;
}

export function isPageReadOnly(effective: EffectivePagePermission | undefined): boolean {
  return !canEditPage(effective);
}

export function canDeletePageComment(
  effective: EffectivePagePermission | undefined,
  commentAuthorId: string,
  currentUserId: string | undefined
): boolean {
  if (!currentUserId) {
    return false;
  }
  if (commentAuthorId === currentUserId && canCommentPage(effective)) {
    return true;
  }
  return canManagePagePermissions(effective);
}

export function canResolvePageComment(effective: EffectivePagePermission | undefined): boolean {
  return canCommentPage(effective);
}
